// @flow

// Modules
import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { find } from 'lodash';

// Components
import Header from 'components/list/header';
import Column from 'components/list/column';
import ListRow, { LINE_TOTAL_HEIGHT, PLACEHOLDER_POS } from 'components/list/list';
import Loader from 'components/loader';

// Methods
import Formatter from 'src/methods/formatter';
import { __ } from 'src/methods/l10n';

// Styles
import { BLUE_GREY, COOL_GREY, BLACK, getGameTypeColorBackground, getGameTypeColorFont } from 'src/styles/palettes';

/**
 * Styles
 */

const BIG_COLUMN_FLEX = 1.8;
const SMALL_COLUMN_FLEX = 0.8;

const PlayersListRoot = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 12em;
  ${(props) => !props.isColumnDisplay && 'max-width: 39em;'}
  ${(props) => !props.isMobile && 'min-width: 28em;'}
  ${(props) => props.isMobile && 'font-size: 1.2em;'}
`;

const EmptyListLabel = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 0.6em;
  width: 100%;
  line-height: 2em;
  color: ${COOL_GREY[200]};
`;

const List = styled.div`
  position: relative;
  width: 100%;
  overflow-y: auto;

  ::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.scrollbarColor} !important;
    border-radius: 9px;
    border: 2px solid #fff2;

    :hover {
      border: 1px solid #fff3;
    }
  }
`;

const Spinner = styled(Loader)`
  margin-top: 3.8em;
`;

const ListContainer = styled.div`
  display: flex;
  flex: 1;
  overflow: auto;
`;

/**
 * Methods
 */

/**
 * Types
 */

type PropTypes = {
  className?: any,
  playersList: ?Array<Object>,
  selectedPlayer: number,
  isMobile: boolean,
  isLoading: boolean,
  isColumnDisplay: boolean,
  selectedChallengeId: string,
  onItemClick: (id: number) => void
};

const PLAYER_POSITIONS = {
  TOP: 'top',
  MIDDLE: 'middle',
  BOTTOM: 'bottom'
};

/**
 * Component
 */

const PlayersList = (props: PropTypes): React$Element<any> => {
  const {
    className,
    playersList,
    selectedPlayer,
    isLoading,
    isMobile,
    isColumnDisplay,
    selectedChallengeId,
    onItemClick
  } = props;
  const [playerPosition, setPlayerPosition] = useState(PLAYER_POSITIONS.MIDDLE);
  let listRef: any = useRef(null);
  const playerInfos = playersList && find(playersList, (player) => player.is_hero);

  const renderListRow = (player: Object, index: number, position?: string) => {
    const { rank, nickname, score, prize_amount } = player;
    const isPrimary = !!prize_amount;
    const isPlayer = playerInfos && player.rank === playerInfos.rank;
    const color = getGameTypeColorFont(selectedChallengeId);
    const colorBackground = getGameTypeColorBackground(selectedChallengeId);

    return (
      <ListRow
        key={`player-${rank}${position ? `-${position}` : ''}`}
        current={index === selectedPlayer}
        color={!isPlayer && isPrimary ? color : ''}
        colorBackground={colorBackground}
        isClickable
        isPlayer={isPlayer}
        position={position}
        onClick={() => onItemClick(index)}>
        <Column color={isPlayer ? BLACK : isPrimary ? color : BLUE_GREY[200]}>{rank}</Column>
        <Column flex={BIG_COLUMN_FLEX}>{nickname}</Column>
        <Column flex={SMALL_COLUMN_FLEX} alignRight>
          {Formatter.formatNumber(score, null, selectedChallengeId === 'GP' ? 0 : 2)}
        </Column>
        <Column flex={SMALL_COLUMN_FLEX} alignRight>
          {prize_amount ? Formatter.formatNumber(prize_amount / 100, '€') : '-'}
        </Column>
      </ListRow>
    );
  };

  const handleScroll = (event: any) => {
    if (event && playerInfos) {
      const { scrollTop } = event.target;
      const { rank } = playerInfos;
      const bounds = event.target.getBoundingClientRect();
      const isTop = scrollTop > (rank - 1) * LINE_TOTAL_HEIGHT;
      const isBottom = scrollTop + bounds.height < rank * LINE_TOTAL_HEIGHT;

      if (isTop && playerPosition !== PLAYER_POSITIONS.TOP) {
        setPlayerPosition(PLAYER_POSITIONS.TOP);
      } else if (isBottom && playerPosition !== PLAYER_POSITIONS.BOTTOM) {
        setPlayerPosition(PLAYER_POSITIONS.BOTTOM);
      } else if (
        !isTop &&
        !isBottom &&
        (playerPosition === PLAYER_POSITIONS.TOP || playerPosition === PLAYER_POSITIONS.BOTTOM)
      ) {
        setPlayerPosition(PLAYER_POSITIONS.MIDDLE);
      }
    }
  };

  // Fake a scroll event on resize
  useEffect(() => {
    const scrollEvent = () => listRef.current.dispatchEvent(new CustomEvent('scroll'));

    window.addEventListener('resize', scrollEvent);

    return () => {
      window.removeEventListener('resize', scrollEvent);
    };
  }, []);

  // Fake a scroll event on list appearance
  useEffect(() => {
    listRef.current.dispatchEvent(new CustomEvent('scroll'));
  }, [playersList]);

  const renderList = () => {
    if (playersList) {
      return playersList.map((player, id) => renderListRow(player, id));
    } else if (isLoading) {
      return <Spinner type={'spinner'} size={0.5} />;
    } else {
      return <EmptyListLabel>{__('No data')}</EmptyListLabel>;
    }
  };

  return (
    <PlayersListRoot className={className} isColumnDisplay={isColumnDisplay} isMobile={isMobile}>
      <Header>
        <Column>{__('Rank')}</Column>
        <Column flex={BIG_COLUMN_FLEX}>{__('Name')}</Column>
        <Column flex={SMALL_COLUMN_FLEX} alignRight>
          {__('Points')}
        </Column>
        <Column flex={SMALL_COLUMN_FLEX} alignRight>
          {__('Guarantee')}
        </Column>
      </Header>
      {playerInfos && playerPosition === PLAYER_POSITIONS.TOP
        ? renderListRow(playerInfos, 0, PLACEHOLDER_POS.TOP)
        : null}
      <ListContainer>
        <List onScroll={handleScroll} ref={listRef} scrollbarColor={getGameTypeColorBackground(selectedChallengeId)}>
          {renderList()}
        </List>
      </ListContainer>
      {playerInfos && playerPosition === PLAYER_POSITIONS.BOTTOM
        ? renderListRow(playerInfos, 0, PLACEHOLDER_POS.BOTTOM)
        : null}
    </PlayersListRoot>
  );
};

export default PlayersList;
